import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notify, notifyError } from '../notify';

// Statistics → Top & pinned: each account's pinned posts and how they move,
// its posts in a subreddit's Top 100 (all time / this year), the posts that
// could get there, and pin advice. Filled by the extension's "Update Post
// Stats" and the scraper's pin watch (backend/helpers/pins.js). Pin, unpin
// and swap here queue a pin task: the extension makes it in the account's own
// browser at the end of the next posting run or Update Post Stats.

const STATUS = {
  new: ['🆕 new', '#22c55e'], climbing: ['📈 climbing', '#22c55e'], holding: ['➖ holding', '#93c5fd'],
  stalled: ['💤 stalled', '#888'], overtaken: ['📉 being overtaken', '#f59e0b'],
  closed: ['🔒 voting closed', '#a5b4fc'], removed: ['⛔ removed', '#ef4444']
};
const KIND = { pin: '📌 Pin this post', swap: '🔁 Swap a pin', unpin: '✖ Unpin this post' };

const fmt = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const a = Math.abs(n);
  if (a >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (a >= 1e4) return Math.round(n / 1e3) + 'K';
  if (a >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(Math.round(n));
};
const ago = (iso) => {
  if (!iso) return '—';
  const h = (Date.now() - Date.parse(iso)) / 36e5;
  return h < 1 ? `${Math.max(1, Math.round(h * 60))} min ago` : h < 48 ? `${Math.round(h)}h ago` : `${Math.round(h / 24)} days ago`;
};
const subLabel = (p) => (p.is_profile_post ? `u/${p.subreddit.replace(/^u_/, '')}` : `r/${p.subreddit}`);
const placeText = (p) => (p.is_profile_post ? 'profile post' : p.rank_all != null ? `#${p.rank_all} all-time`
  : p.rank_year != null ? `#${p.rank_year} this year` : p.list_state === 'pending' ? 'rank pending' : 'not in Top 100');
const safeUrl = (u) => (/^https:\/\/(www\.)?reddit\.com\//i.test(String(u || '')) ? u : null);
const clock = (iso) => new Date(iso).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
const pointPlace = (p) => (p.rank_all != null ? `#${p.rank_all} all-time` : p.rank_year != null ? `#${p.rank_year} this year` : 'outside the Top 100');

// A post's days: its upvotes, what it gained each day, and its place in the
// Top 100 (all time, this year). One reading per day (UTC; a later read the
// same day updates it), so each day compares with the day before. Upvotes,
// daily gains and places have different scales, so each has its own panel on
// the shared day axis, with one crosshair and readout.
const C_UP = '#ff4500', C_ALL = '#3987e5', C_YEAR = '#199e70';
const DAY_MS = 864e5;

// The last reading of each day (older rows have no `day`: taken from the
// time), with the change from the reading before. After a day without a
// reading, `gained` covers `over` days and `per_day` is its daily average.
function dailyReadings(points) {
  const byDay = new Map();
  for (const p of points) if (p.score != null) byDay.set(p.day || String(p.checked_at).slice(0, 10), p);
  const days = [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([day, p]) => ({ ...p, day }));
  return days.map((d, i) => {
    const prev = days[i - 1];
    const move = (key) => (prev && d[key] != null && prev[key] != null ? prev[key] - d[key] : null); // + = climbed
    const over = prev ? Math.max(1, Math.round((Date.parse(`${d.day}T00:00:00Z`) - Date.parse(`${prev.day}T00:00:00Z`)) / DAY_MS)) : null;
    const gained = prev ? d.score - prev.score : null;
    return {
      ...d, gained, over, per_day: gained == null ? null : gained / over, move_all: move('rank_all'), move_year: move('rank_year'),
      views_gained: prev && d.views != null && prev.views != null ? d.views - prev.views : null
    };
  });
}

const dayLabel = (day, withWeekday) => new Date(`${day}T12:00:00Z`).toLocaleDateString([], { ...(withWeekday ? { weekday: 'short' } : {}), day: 'numeric', month: 'short' });
const signed = (n) => (n == null ? '—' : n > 0 ? `+${Number(n).toLocaleString()}` : n < 0 ? `−${Number(-n).toLocaleString()}` : '0');
const placeMove = (n) => (n == null ? '' : n > 0 ? ` ▲${n}` : n < 0 ? ` ▼${-n}` : ' =');

// SVG path of a series, broken where a value is missing (outside the Top 100)
function linePath(pts, xOf, yOf, key) {
  let d = '', open = false;
  for (const p of pts) {
    if (p[key] == null) { open = false; continue; }
    d += `${open ? 'L' : 'M'}${xOf(p).toFixed(1)},${yOf(p[key]).toFixed(1)}`;
    open = true;
  }
  return d;
}

function HistoryChart({ postId }) {
  const [hist, setHist] = useState(null);
  const [hover, setHover] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/api/pins/posts/${postId}/history`)
      .then(r => { if (live) setHist(r.data); })
      .catch(e => { if (live) setError(e.response?.data?.error || e.message); });
    return () => { live = false; };
  }, [postId]);

  if (error) return <p style={{ color: '#ef4444', fontSize: '12px', margin: '8px 0' }}>{error}</p>;
  if (!hist) return <p style={{ color: '#666', fontSize: '12px', margin: '8px 0' }}>Loading readings…</p>;
  const days = dailyReadings(hist.points || []);
  const top = hist.top_all;
  const thresholds = top?.score_100 != null && (
    <span style={{ color: '#666' }}> · r/{hist.post.subreddit} all-time: #100 at {fmt(top.score_100)}, #25 at {fmt(top.score_25)}, #10 at {fmt(top.score_10)}</span>
  );
  const table = days.length > 0 && (
    <details style={{ marginTop: '6px' }}>
      <summary style={{ fontSize: '11px', color: '#666', cursor: 'pointer' }}>Daily changes ({days.length} day{days.length > 1 ? 's' : ''})</summary>
      <table style={{ fontSize: '11px', color: '#aaa', borderCollapse: 'collapse', marginTop: '6px' }}>
        <thead><tr>{['Day', 'Upvotes', 'Gained', 'All-time', 'This year', 'Views', 'Pinned'].map(h => <th key={h} style={{ textAlign: 'left', padding: '2px 12px 2px 0', color: '#777' }}>{h}</th>)}</tr></thead>
        <tbody>
          {[...days].reverse().map(d => (
            <tr key={d.day}>
              <td style={{ padding: '2px 12px 2px 0' }}>{dayLabel(d.day, true)}</td>
              <td>{Number(d.score).toLocaleString()}</td>
              <td>{signed(d.gained)}{d.over > 1 ? ` (${d.over} days)` : ''}</td>
              <td>{d.rank_all != null ? `#${d.rank_all}${placeMove(d.move_all)}` : '—'}</td>
              <td>{d.rank_year != null ? `#${d.rank_year}${placeMove(d.move_year)}` : '—'}</td>
              <td>{d.views != null ? `${fmt(d.views)}${d.views_gained != null ? ` (${signed(d.views_gained)})` : ''}` : '—'}</td>
              <td>{d.pinned ? 'yes' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
  if (days.length < 2) {
    const d0 = days[0];
    return (
      <div style={{ marginTop: '10px', fontSize: '12px', color: '#aaa', lineHeight: 1.6 }}>
        <div>Day by day: upvotes, gained per day, place in the Top 100{thresholds}</div>
        {d0
          ? <div>Tracked since {dayLabel(d0.day, true)}: <strong style={{ color: '#fff' }}>▲ {Number(d0.score).toLocaleString()}</strong>, {pointPlace(d0)}. One point per day: the graph and the daily changes start with the second day.</div>
          : <div>No readings yet.</div>}
        {table}
      </div>
    );
  }

  // Layout: upvotes, gained per day, place; one day axis
  const W = 640, L = 52, R = 16;
  const T1 = 20, H1 = 90, T2 = T1 + H1 + 30, H2 = 60, T3 = T2 + H2 + 30, H3 = 80, AX = T3 + H3 + 16, H = AX + 8;
  const d0 = Date.parse(`${days[0].day}T00:00:00Z`), dN = Date.parse(`${days[days.length - 1].day}T00:00:00Z`);
  const span = Math.max(1, Math.round((dN - d0) / DAY_MS));
  const xOf = (d) => L + ((Date.parse(`${d.day}T00:00:00Z`) - d0) / DAY_MS / span) * (W - L - R);
  const lo = Math.min(...days.map(d => d.score)), hi = Math.max(...days.map(d => d.score));
  const pad = Math.max(1, (hi - lo) * 0.1);
  const u0 = Math.max(0, lo - pad), u1 = hi + pad;
  const yUp = (v) => T1 + (1 - (v - u0) / (u1 - u0 || 1)) * H1;
  const gains = days.map(d => d.per_day).filter(g => g != null);
  const g0 = Math.min(0, ...gains), g1 = Math.max(0, ...gains, 1);
  const yGain = (v) => T2 + (1 - (v - g0) / (g1 - g0 || 1)) * H2;
  const barW = Math.max(3, Math.min(22, ((W - L - R) / (span + 1)) * 0.6));
  const ranks = days.flatMap(d => [d.rank_all, d.rank_year]).filter(r => r != null);
  let r0 = ranks.length ? Math.max(1, Math.min(...ranks) - 2) : 1, r1 = ranks.length ? Math.min(100, Math.max(...ranks) + 2) : 100;
  if (r1 - r0 < 6) { r0 = Math.max(1, r0 - 3); r1 = Math.min(100, r1 + 3); }
  const yRank = (r) => T3 + ((r - r0) / (r1 - r0 || 1)) * H3; // #1 at the top
  const pinDay = hist.post.pinned_at ? String(hist.post.pinned_at).slice(0, 10) : null;
  const pinX = pinDay && pinDay >= days[0].day && pinDay <= days[days.length - 1].day ? xOf({ day: pinDay }) : null;
  const labelEvery = Math.max(1, Math.ceil(days.length / 8));
  const onMove = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - box.left) / box.width * W;
    let best = 0;
    days.forEach((d, i) => { if (Math.abs(xOf(d) - px) < Math.abs(xOf(days[best]) - px)) best = i; });
    setHover(best);
  };
  const hp = hover != null ? days[hover] : null;
  const grid = (y) => <line x1={L} x2={W - R} y1={y} y2={y} stroke="#2a2a2a" strokeWidth="1" />;
  const yLabel = (y, text) => <text x={L - 6} y={y + 4} fill="#777" fontSize="10" textAnchor="end">{text}</text>;
  const dots = (key, color, yOf) => days.filter(d => d[key] != null).map(d => <circle key={`${key}${d.day}`} cx={xOf(d)} cy={yOf(d[key])} r="2.5" fill={color} />);
  const key = (color, label, x0) => (
    <g>
      <line x1={x0} x2={x0 + 14} y1={T3 - 12} y2={T3 - 12} stroke={color} strokeWidth="2" />
      <text x={x0 + 18} y={T3 - 8} fill="#aaa" fontSize="10">{label}</text>
    </g>
  );

  return (
    <div style={{ marginTop: '10px' }}>
      <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>
        Day by day{pinX != null ? ' · dashed line = pinned' : ''}{thresholds}
      </div>
      <div style={{ position: 'relative' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: `${W}px`, display: 'block' }} onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img" aria-label="Upvotes, upvotes gained per day and place in the Top 100, day by day">
          {/* upvotes */}
          <text x={L} y={T1 - 8} fill="#aaa" fontSize="10">Upvotes</text>
          {[u0, (u0 + u1) / 2, u1].map((v, i) => <g key={i}>{grid(yUp(v))}{yLabel(yUp(v), fmt(v))}</g>)}
          <path d={linePath(days, xOf, yUp, 'score')} fill="none" stroke={C_UP} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {dots('score', C_UP, yUp)}
          {/* gained per day */}
          <text x={L} y={T2 - 8} fill="#aaa" fontSize="10">Gained per day</text>
          {grid(yGain(g1))}{yLabel(yGain(g1), `+${fmt(g1)}`)}
          <line x1={L} x2={W - R} y1={yGain(0)} y2={yGain(0)} stroke="#444" strokeWidth="1" />{yLabel(yGain(0), '0')}
          {g0 < 0 && <>{grid(yGain(g0))}{yGain(g0) - yGain(0) >= 10 && yLabel(yGain(g0), `−${fmt(-g0)}`)}</>}
          {days.filter(d => d.per_day != null && d.per_day !== 0).map(d => {
            const yA = yGain(Math.max(0, d.per_day)), yB = yGain(Math.min(0, d.per_day));
            return <rect key={`g${d.day}`} x={xOf(d) - barW / 2} y={yA} width={barW} height={Math.max(1, yB - yA)} rx="2" fill={C_UP} opacity={d.per_day < 0 ? 0.5 : 0.85} />;
          })}
          {/* place in the Top 100 */}
          <text x={L} y={T3 - 8} fill="#aaa" fontSize="10">Place</text>
          {key(C_ALL, 'all-time', L + 40)}
          {key(C_YEAR, 'this year', L + 110)}
          {ranks.length ? (
            <>
              {[r0, Math.round((r0 + r1) / 2), r1].map((r, i) => <g key={i}>{grid(yRank(r))}{yLabel(yRank(r), `#${r}`)}</g>)}
              <path d={linePath(days, xOf, yRank, 'rank_all')} fill="none" stroke={C_ALL} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              <path d={linePath(days, xOf, yRank, 'rank_year')} fill="none" stroke={C_YEAR} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {dots('rank_all', C_ALL, yRank)}{dots('rank_year', C_YEAR, yRank)}
            </>
          ) : (
            <>{grid(T3 + H3 / 2)}<text x={L + (W - L - R) / 2} y={T3 + H3 / 2 - 6} fill="#666" fontSize="11" textAnchor="middle">Not in a Top 100 (all time or this year) on these days</text></>
          )}
          {/* day axis */}
          {days.map((d, i) => ((i % labelEvery === 0 && days.length - 1 - i >= labelEvery / 2) || i === days.length - 1) && (
            <text key={`x${d.day}`} x={xOf(d)} y={AX} fill="#777" fontSize="10" textAnchor={i === 0 ? 'start' : i === days.length - 1 ? 'end' : 'middle'}>{dayLabel(d.day)}</text>
          ))}
          {pinX != null && (
            <g>
              <line x1={pinX} x2={pinX} y1={T1} y2={T3 + H3} stroke="#aaa" strokeWidth="1" strokeDasharray="4 3" />
              <text x={pinX + (pinX > W - 80 ? -4 : 4)} y={T1 + 10} fill="#aaa" fontSize="10" textAnchor={pinX > W - 80 ? 'end' : 'start'}>pinned</text>
            </g>
          )}
          {hp && (
            <g>
              <line x1={xOf(hp)} x2={xOf(hp)} y1={T1} y2={T3 + H3} stroke="#555" strokeWidth="1" />
              <circle cx={xOf(hp)} cy={yUp(hp.score)} r="4" fill={C_UP} stroke="#1a1a1a" strokeWidth="2" />
              {hp.rank_all != null && <circle cx={xOf(hp)} cy={yRank(hp.rank_all)} r="4" fill={C_ALL} stroke="#1a1a1a" strokeWidth="2" />}
              {hp.rank_year != null && <circle cx={xOf(hp)} cy={yRank(hp.rank_year)} r="4" fill={C_YEAR} stroke="#1a1a1a" strokeWidth="2" />}
            </g>
          )}
        </svg>
        {hp && (
          <div style={{ position: 'absolute', top: 0, left: `${Math.min(66, xOf(hp) / W * 100)}%`, background: '#111', border: '1px solid #333', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', color: '#aaa', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
            <div>{dayLabel(hp.day, true)}{hp.pinned ? ' · pinned' : ''}</div>
            <div><span style={{ color: '#fff', fontWeight: 600, fontSize: '13px' }}>▲ {Number(hp.score).toLocaleString()}</span> upvotes{hp.gained != null ? `, ${signed(hp.gained)} ${hp.over > 1 ? `over ${hp.over} days` : 'that day'}` : ''}</div>
            <div><span style={{ color: '#fff', fontWeight: 600 }}>{hp.rank_all != null ? `#${hp.rank_all}` : '—'}</span> all-time{placeMove(hp.move_all)} · <span style={{ color: '#fff', fontWeight: 600 }}>{hp.rank_year != null ? `#${hp.rank_year}` : '—'}</span> this year{placeMove(hp.move_year)}</div>
            {hp.views != null && <div>{fmt(hp.views)} views{hp.views_gained != null ? ` (${signed(hp.views_gained)})` : ''}</div>}
          </div>
        )}
      </div>
      {table}
    </div>
  );
}

function PostRow({ p, extra, open, onToggle, action }) {
  const [label, color] = STATUS[p.status] || [p.status, '#888'];
  const e = p.pin_effect;
  const facts = [
    `▲ ${fmt(p.score)}`,
    p.votes_per_day != null ? `${p.votes_per_day > 0 ? '+' : ''}${fmt(p.votes_per_day)} votes/day` : null,
    p.weekly_visitors ? `👀 ${fmt(p.weekly_visitors)} weekly` : null,
    p.next_place && p.votes_open ? `#${p.next_place.place} needs +${fmt(p.next_place.gap)}` : null,
    p.best_rank_all != null && (p.rank_all == null || p.best_rank_all < p.rank_all) ? `best #${p.best_rank_all} all-time` : null,
    p.age_days != null ? (p.age_days < 1 ? 'posted today' : `posted ${Math.round(p.age_days)}d ago`) : null,
    e && e.since_per_day != null ? `pinned: ${e.since_per_day >= 0 ? '+' : ''}${fmt(e.since_per_day)}/day${e.before_per_day != null ? ` (before ${e.before_per_day >= 0 ? '+' : ''}${fmt(e.before_per_day)})` : ''}` : null,
    e && e.views_gain && e.views_gain.gain > 0 ? `+${fmt(e.views_gain.gain)} views since ${new Date(e.views_gain.from).toLocaleDateString()}` : null,
    e ? (e.pinned_at ? `pinned ${ago(e.pinned_at)}` : 'pinned before tracking') : null,
    p.pinned && p.pin_order ? (p.pin_order === 1 ? '1st pin (top)' : `pin #${p.pin_order} on the profile`) : null,
    p.kept_on_top ? '⭐ always on top' : null,
    ...(extra || [])
  ].filter(Boolean);
  return (
    <div>
      <div className="list-item" onClick={onToggle} style={{ alignItems: 'flex-start', padding: '12px 16px', cursor: 'pointer' }}>
        <div className="list-item-main" style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ color: '#ff4500' }}>{subLabel(p)}</strong>
            {safeUrl(p.url) && <a href={safeUrl(p.url)} target="_blank" rel="noopener noreferrer" onClick={ev => ev.stopPropagation()} style={{ fontSize: '12px', color: '#aaa' }}>Open ↗</a>}
          </div>
          <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
          <div style={{ display: 'flex', gap: '14px', marginTop: '6px', fontSize: '12px', color: '#888', flexWrap: 'wrap' }}>
            {facts.map((f, i) => <span key={i}>{f}</span>)}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>{placeText(p)}</div>
          {p.rank_change_7d ? <div style={{ fontSize: '11px', color: p.rank_change_7d < 0 ? '#22c55e' : '#ef4444' }}>{p.rank_change_7d < 0 ? `▲${-p.rank_change_7d}` : `▼${p.rank_change_7d}`} this week</div> : null}
          <div style={{ fontSize: '11px', color, marginTop: '4px' }}>{label}</div>
          {action && <div style={{ marginTop: '6px' }} onClick={ev => ev.stopPropagation()}>{action}</div>}
        </div>
      </div>
      {open && <div style={{ padding: '0 16px 12px' }}><HistoryChart postId={p.id} /></div>}
    </div>
  );
}

function Section({ title, posts, empty, extra, openId, setOpenId, action }) {
  if (!posts.length && !empty) return null;
  return (
    <div style={{ marginTop: '14px' }}>
      <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{title}</div>
      {posts.length ? (
        <div className="list">
          {posts.map(p => <PostRow key={p.id} p={p} extra={extra ? extra(p) : null} open={openId === p.id} onToggle={() => setOpenId(openId === p.id ? null : p.id)} action={action ? action(p) : null} />)}
        </div>
      ) : <p className="empty" style={{ margin: 0 }}>{empty}</p>}
    </div>
  );
}

const smallBtn = { fontSize: '11px', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer', border: '1px solid #444', background: '#222', color: '#ddd' };
const TASK_STATUS = { queued: ['⏳', '#f59e0b', 'waiting for the extension'], done: ['✓', '#22c55e', 'done'], failed: ['⚠', '#ef4444', 'failed'], cancelled: ['✕', '#666', 'cancelled'] };
const taskLabel = (t) => (t.to_top ? `Put ${subLabel(t.pin)} on top` : t.pin && t.unpin ? `Swap: unpin ${subLabel(t.unpin)}, pin ${subLabel(t.pin)}` : t.pin ? `Pin ${subLabel(t.pin)}` : `Unpin ${subLabel(t.unpin)}`);

// The account's pin tasks: waiting ones can be cancelled, finished ones show
// what happened (last 3 days).
function TaskList({ tasks, onCancel, busy }) {
  if (!tasks.length) return null;
  return (
    <div style={{ marginTop: '10px', fontSize: '12px', color: '#aaa', lineHeight: 1.7 }}>
      {tasks.map(t => {
        const [icon, color, word] = TASK_STATUS[t.status] || ['•', '#888', t.status];
        return (
          <div key={t.id}>
            <span style={{ color }}>{icon} {taskLabel(t)}</span> — {word}{t.error ? `: ${t.error}` : ''}
            <span style={{ color: '#666' }}> · asked by {t.requested_by_name || 'someone'} {ago(t.requested_at)}{t.done_at && t.status !== 'queued' ? ` · ${t.done_by_name || ''} ${ago(t.done_at)}` : ''}</span>
            {t.status === 'queued' && <button style={{ ...smallBtn, marginLeft: '8px' }} disabled={busy[t.id]} onClick={() => onCancel(t.id)}>Cancel</button>}
          </div>
        );
      })}
    </div>
  );
}

// All 4 pins in use: which one makes room for the new post
function SwapChooser({ account, post, onPick, onClose }) {
  const box = useRef(null);
  // Pin may be clicked far down the list: bring the question into view
  useEffect(() => { box.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [post.id]);
  return (
    <div ref={box} style={{ border: '1px solid #f59e0b', borderRadius: '8px', padding: '10px 12px', marginTop: '10px', fontSize: '12px', color: '#ddd' }}>
      All {account.pin_limit} pins are in use. Which one should make room for <strong>{subLabel(post)}</strong> — {post.title}? (The ⭐ always-on-top pin stays.)
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
        {account.pinned.filter(p => !p.kept_on_top).map(p => (
          <button key={p.id} style={smallBtn} onClick={() => onPick(p)}>Unpin {subLabel(p)} · {placeText(p)}</button>
        ))}
        <button style={{ ...smallBtn, background: 'transparent' }} onClick={onClose}>Never mind</button>
      </div>
    </div>
  );
}

// onReadReddit(ids?): Statistics' "read from Reddit now" (the scraper checks
// those accounts at once, then the page reloads); readingReddit: its progress.
export default function PinsTab({ creatorId, accountId, onReadReddit, readingReddit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState({});
  const [swapFor, setSwapFor] = useState(null); // { accountId, post }
  const [search, setSearch] = useState({});     // account id → text
  const [showOthers, setShowOthers] = useState({});
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (!creatorId && !accountId) return;
    const s = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams(accountId ? { account_id: accountId } : { creator_id: creatorId });
      const res = await axios.get(`${API}/api/pins/overview?${qs}`);
      if (s === seq.current) setData(res.data);
    } catch (e) {
      if (s === seq.current) setError(e.response?.data?.error || e.message);
    }
    if (s === seq.current) setLoading(false);
  }, [creatorId, accountId]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await axios.post(`${API}/api/pins/advice/${id}/decide`, { decision });
      notify(decision === 'done' ? 'Marked done — the next read confirms it on Reddit' : 'Hidden until the post climbs further');
      await load();
    } catch (e) { notifyError(e, 'Could not save'); }
    setBusy(b => ({ ...b, [id]: false }));
  };

  const run = async (key, fn, done) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); if (done) notify(done); await load(); } catch (e) { notifyError(e, 'Could not save'); }
    setBusy(b => ({ ...b, [key]: false }));
  };
  const QUEUED = 'Queued: the extension does it in the account\'s browser at the end of the next posting run or Update Post Stats';
  const queue = (accountIdOf, body, key) => run(key, () => axios.post(`${API}/api/pins/tasks`, { account_id: accountIdOf, ...body }), QUEUED);
  const cancelTask = (id) => run(id, () => axios.post(`${API}/api/pins/tasks/${id}/cancel`), 'Cancelled');
  const keepOnTop = (accountIdOf, postId, key) => run(key, () => axios.post(`${API}/api/pins/keep-on-top`, { account_id: accountIdOf, post_id: postId }),
    postId ? `Always on top. ${QUEUED}` : 'Always-on-top is off');

  // Pin / Unpin on a post row; a waiting task shows instead of the button
  const rowAction = (a) => (p) => {
    const waiting = (a.tasks || []).find(t => t.status === 'queued' && (t.pin?.id === p.id || t.unpin?.id === p.id));
    if (waiting) return <span style={{ fontSize: '11px', color: '#f59e0b' }}>⏳ {waiting.pin?.id === p.id ? 'pin' : 'unpin'} queued</span>;
    if (p.pinned) {
      return (
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {p.kept_on_top
            ? <button style={smallBtn} disabled={busy[p.id]} title="Stop putting this pin back on top after other pins" onClick={() => keepOnTop(a.account_id, null, p.id)}>⭐ Turn off always-on-top</button>
            : <button style={smallBtn} disabled={busy[p.id]} title="Make this the first pin and keep it there: after any other pin the extension puts it back on top" onClick={() => keepOnTop(a.account_id, p.id, p.id)}>⭐ Always on top</button>}
          <button style={smallBtn} disabled={busy[p.id]} onClick={() => window.confirm(`Unpin ${subLabel(p)} from u/${a.reddit_username}'s profile?`) && queue(a.account_id, { unpin_post_id: p.id }, p.id)}>Unpin</button>
        </div>
      );
    }
    if (p.removed) return null;
    const full = a.pinned.length >= a.pin_limit;
    return <button style={smallBtn} disabled={busy[p.id]} onClick={() => (full ? setSwapFor({ accountId: a.account_id, post: p }) : queue(a.account_id, { pin_post_id: p.id }, p.id))}>📌 Pin</button>;
  };

  if (error) return <p style={{ color: '#ef4444', padding: '12px' }}>{error}</p>;
  if (!data) return <p style={{ color: '#666', textAlign: 'center', padding: '24px' }}>{loading ? 'Loading pinned and top posts…' : 'No data.'}</p>;
  const accounts = data.accounts || [];

  return (
    <div style={{ opacity: loading ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', color: '#666', maxWidth: '760px' }}>
          Posts ranked against their subreddit's Top 100 (all time and this year). Pin advice weighs the place a post is heading for
          by the sub's weekly visitors, so a post climbing in a big sub goes first. Read by <strong>Update Post Stats</strong> in the extension
          and re-checked by the scraper every few hours. <strong>Pin</strong>, <strong>Unpin</strong> and <strong>Add to tasks</strong> queue the change: the extension makes it
          in the account's own browser at the end of the next posting run or Update Post Stats (the summary window there also has "Pin now").
          <strong> ⭐ Always on top</strong>: one pin per account (usually the post with the link) goes first and stays there. Reddit puts every new pin on top, so the extension re-pins it after other pins.
        </div>
        {onReadReddit
          ? <button className="btn-secondary" onClick={() => onReadReddit()} disabled={!!readingReddit} style={{ fontSize: '12px', padding: '5px 12px' }}
              title="The scraper reads these profiles now: pins, their order, top posts. Usually a minute or two.">
              {readingReddit ? `Reading Reddit… ${readingReddit.done}/${readingReddit.total}` : '↻ Read from Reddit now'}
            </button>
          : <button className="btn-secondary" onClick={load} disabled={loading} style={{ fontSize: '12px', padding: '5px 12px' }}>{loading ? 'Loading…' : 'Reload'}</button>}
      </div>
      {!accounts.length && <p className="empty">No accounts.</p>}
      {accounts.map(a => {
        const tracked = a.pinned.length + a.top.length + a.contenders.length;
        return (
          <div key={a.account_id} style={{ borderTop: '1px solid #2a2a2a', marginTop: '16px', paddingTop: '14px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '15px' }}>u/{a.reddit_username}</strong>
              <span style={{ fontSize: '12px', color: '#888' }}>📌 {a.pinned.length} / {a.pin_limit} pins</span>
              <span style={{ fontSize: '12px', color: '#888' }}>🏆 {a.top.length} more in a Top 100</span>
              <span style={{ fontSize: '12px', color: '#666' }}>
                scraper check: {a.watch?.checked_at ? ago(a.watch.checked_at) : 'not yet'}{a.watch?.next_check_at ? ` · next ${clock(a.watch.next_check_at)}` : ''}
                {a.lists_pending ? ` · ${a.lists_pending} Top list(s) still being read` : ''}
                {a.keep_on_top_post_id ? ` · ⭐ always on top: ${(() => { const k = a.pinned.find(p => p.id === a.keep_on_top_post_id); return k ? subLabel(k) : 'a post no longer pinned'; })()}` : ''}
                {a.watch?.last_error ? ` · ⚠ ${a.watch.last_error}` : ''}
              </span>
              {onReadReddit && (
                <button style={smallBtn} disabled={!!readingReddit} onClick={() => onReadReddit([a.account_id])}
                  title="The scraper reads this profile now: pins, their order, top posts. Usually a minute or two.">↻ Check now</button>
              )}
            </div>

            {a.advice.map(ad => (
              <div key={ad.id} style={{ background: '#1a1208', border: '1px solid #ff4500', borderRadius: '10px', padding: '12px 14px', marginTop: '10px' }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>{KIND[ad.kind] || ad.kind}</div>
                <div style={{ fontSize: '13px', color: '#ddd', lineHeight: 1.5 }}>{ad.reason}</div>
                {[ad.kind === 'swap' ? ['1. Unpin', ad.unpin] : null, ad.kind === 'unpin' ? ['Unpin', ad.unpin] : null, ad.kind !== 'unpin' ? [ad.kind === 'swap' ? '2. Pin' : 'Pin', ad.pin] : null]
                  .filter(s => s && s[1]).map(([lbl, p]) => (
                    <div key={lbl} style={{ fontSize: '12px', color: '#bbb', marginTop: '4px' }}>
                      {lbl} <strong style={{ color: '#fff' }}>{subLabel(p)}</strong> — {p.title} · {placeText(p)}
                      {safeUrl(p.url) && <> · <a href={safeUrl(p.url)} target="_blank" rel="noopener noreferrer" style={{ color: '#ff4500' }}>Open ↗</a></>}
                    </div>
                  ))}
                <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>By hand instead: open the post → ⋯ menu → Pin to profile (or Unpin), then "Done by hand".</div>
                {(() => {
                  const waiting = (a.tasks || []).find(t => t.status === 'queued' && t.advice_id === ad.id);
                  return (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {waiting
                        ? <span style={{ fontSize: '12px', color: '#f59e0b' }}>⏳ Waiting for the extension <button style={{ ...smallBtn, marginLeft: '6px' }} disabled={busy[waiting.id]} onClick={() => cancelTask(waiting.id)}>Cancel</button></span>
                        : <button disabled={busy[ad.id]} onClick={() => queue(a.account_id, { advice_id: ad.id }, ad.id)} style={{ background: '#22c55e', color: '#0f0f0f', border: 'none', borderRadius: '6px', padding: '6px 14px', fontWeight: 600, cursor: 'pointer' }}>{ad.kind === 'swap' ? '🔁 Add swap to tasks' : ad.kind === 'unpin' ? '✖ Add unpin to tasks' : '📌 Add pin to tasks'}</button>}
                      <button disabled={busy[ad.id]} onClick={() => decide(ad.id, 'done')} className="btn-secondary" style={{ fontSize: '12px', padding: '6px 14px' }}>Done by hand</button>
                      <button disabled={busy[ad.id]} onClick={() => decide(ad.id, 'dismissed')} className="btn-secondary" style={{ fontSize: '12px', padding: '6px 14px' }}>Not now</button>
                    </div>
                  );
                })()}
              </div>
            ))}

            <TaskList tasks={a.tasks || []} onCancel={cancelTask} busy={busy} />
            {swapFor && swapFor.accountId === a.account_id && (
              <SwapChooser account={a} post={swapFor.post} onClose={() => setSwapFor(null)}
                onPick={(old) => { setSwapFor(null); queue(a.account_id, { pin_post_id: swapFor.post.id, unpin_post_id: old.id }, swapFor.post.id); }} />
            )}

            <Section title="📍 Pinned now" posts={a.pinned} empty="Nothing pinned on this profile." openId={openId} setOpenId={setOpenId} action={rowAction(a)} />
            <Section title="🏆 In a subreddit's Top 100 (not pinned)" posts={a.top} openId={openId} setOpenId={setOpenId} action={rowAction(a)} />
            <Section title="🎯 Could make a Top 100" posts={a.contenders} openId={openId} setOpenId={setOpenId} action={rowAction(a)}
              extra={p => [p.projected_rank_all != null ? `heading for #${p.projected_rank_all} all-time` : p.projected_rank_year != null ? `heading for #${p.projected_rank_year} this year` : null]} />
            <Section title="⛔ Removed in the last 3 days" posts={a.removed} openId={openId} setOpenId={setOpenId} action={rowAction(a)} extra={p => [`removed: ${p.removed}`]} />
            {(a.others || []).length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <button className="btn-secondary" style={{ fontSize: '12px', padding: '5px 12px' }} onClick={() => setShowOthers(o => ({ ...o, [a.account_id]: !o[a.account_id] }))}>
                  {showOthers[a.account_id] ? '▾' : '▸'} 🔎 Other posts to pin by hand ({a.others.length}, best first)
                </button>
                {showOthers[a.account_id] && (
                  <>
                    <input value={search[a.account_id] || ''} onChange={e => setSearch(q => ({ ...q, [a.account_id]: e.target.value }))} placeholder="Search by subreddit or title"
                      style={{ display: 'block', marginTop: '8px', width: '100%', maxWidth: '360px', fontSize: '12px', padding: '6px 10px', background: '#111', color: '#ddd', border: '1px solid #333', borderRadius: '6px' }} />
                    <Section title="" posts={a.others.filter(p => { const q = (search[a.account_id] || '').trim().toLowerCase(); return !q || `${p.subreddit} ${p.title || ''}`.toLowerCase().includes(q); })}
                      empty="No post matches." openId={openId} setOpenId={setOpenId} action={rowAction(a)} />
                  </>
                )}
              </div>
            )}
            {!tracked && !a.pinned.length && (
              <p className="empty" style={{ marginTop: '10px' }}>No readings yet — run <strong>Update Post Stats</strong> in the extension, or wait for the scraper's first check.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
